// Marcadores.
//
// São KEYWORDS do IMAP: vivem na mensagem, no servidor, e aparecem também no
// Thunderbird e no celular. Não são invenção do webmail — por isso não há
// "lista de marcadores" a administrar: existe o que estiver em uso, e um
// marcador sem nenhuma mensagem simplesmente deixa de existir.

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as correio from "../api/correio";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";

/**
 * As flags do sistema começam com barra invertida (\Seen, \Flagged). Nunca são
 * marcadores do usuário, e mostrá-las na lista encheria a tela de coisas que
 * ele não criou e não pode tirar.
 */
export function apenasDoUsuario(marcadores: string[] | undefined): string[] {
  return (marcadores ?? []).filter((m) => !m.startsWith("\\"));
}

/**
 * Cor derivada do nome, como a do avatar.
 *
 * Determinística: "Clientes" tem a mesma cor em toda mensagem e em toda
 * sessão. Guardar a cor escolhida exigiria uma tabela de marcadores — e o
 * IMAP não tem onde guardá-la, então ela se perderia ao abrir no Thunderbird.
 */
const CORES = [
  "#0f766e", "#b45309", "#be123c", "#4f46e5",
  "#15803d", "#0369a1", "#a16207", "#6d28d9",
];

export function corDoMarcador(nome: string): string {
  let soma = 0;
  for (let i = 0; i < nome.length; i++) soma = (soma * 31 + nome.charCodeAt(i)) >>> 0;
  return CORES[soma % CORES.length]!;
}

export function Etiqueta({ nome }: { nome: string }) {
  return (
    <span
      className="mr-1.5 rounded px-1.5 py-0.5 text-xs font-normal text-white"
      style={{ background: corDoMarcador(nome) }}
    >
      {nome}
    </span>
  );
}

/** O menu de aplicar e tirar marcador de uma mensagem. */
export function MenuDeMarcadores({
  uuid,
  atuais,
  sugestoes,
}: {
  uuid: string;
  atuais: string[];
  sugestoes: string[];
}) {
  const fila = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [novo, setNovo] = useState("");
  const [erro, setErro] = useState("");
  const caixa = useRef<HTMLDivElement>(null);

  // OS MARCADORES DA CAIXA INTEIRA, não os da tela.
  //
  // Apontamento 20 da revisão de UX: "Marcadores não lista os existentes —
  // só um campo 'Novo marcador e Enter'". O menu LISTAVA sugestões, e elas
  // vinham de `sugestoes` — os marcadores das mensagens CARREGADAS na lista.
  // Abrindo uma mensagem direto pelo endereço, o que estava carregado era
  // aquela mensagem: sugestão nenhuma, e o menu de fato virava só o campo de
  // criar. A impressão estava certa; o diagnóstico "não lista" também, para
  // todos os efeitos.
  //
  // Só busca com o menu ABERTO: são dezenas destes menus numa lista de
  // mensagens, e carregar todos ao desenhar a lista seria dezenas de
  // requisições para um menu que ninguém abriu.
  const daCaixa = useQuery({
    queryKey: ["marcadores-em-uso"],
    queryFn: ({ signal }) => correio.marcadoresEmUso(signal),
    enabled: aberto,
  });

  useEffect(() => {
    function fora(e: MouseEvent) {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const aplicar = useMutation({
    mutationFn: (lista: string[]) => correio.marcadores(uuid, lista),
    onSuccess: () => {
      setErro("");
      void fila.invalidateQueries({ queryKey: ["mensagens"] });
      void fila.invalidateQueries({ queryKey: ["mensagem", uuid] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const doUsuario = apenasDoUsuario(atuais);

  function alternar(nome: string) {
    const lista = doUsuario.includes(nome)
      ? doUsuario.filter((m) => m !== nome)
      : [...doUsuario, nome];
    aplicar.mutate(lista);
  }

  // Sugestões são os marcadores JÁ em uso na caixa, menos os que esta
  // mensagem já tem. Sem isso, cada mensagem receberia um marcador escrito de
  // um jeito diferente e a lista viraria "Clientes", "clientes", "Cliente".
  //
  // As duas fontes se SOMAM: a do servidor é a verdade sobre a caixa, e a
  // `sugestoes` (o que está na tela) continua valendo porque chega primeiro —
  // o menu abre preenchido em vez de piscar vazio esperando a requisição.
  const todos = [
    ...new Set([...(daCaixa.data?.itens ?? []).map((m) => m.nome), ...sugestoes]),
  ].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const disponiveis = todos.filter((m) => !doUsuario.includes(m));

  // O CAMPO FILTRA E CRIA, e é o mesmo campo.
  //
  // O ajuste pedido foi "lista dos marcadores com busca". Dois campos — um
  // de buscar e um de criar — seriam dois num menu de 240px, e a pessoa
  // erraria qual é qual. Digitando, a lista se estreita; batendo Enter, o que
  // foi digitado é aplicado como marcador. Quem tem três marcadores usa como
  // criação; quem tem trinta, como busca.
  const procurado = novo.trim().toLowerCase();
  const filtrados = procurado
    ? disponiveis.filter((m) => m.toLowerCase().includes(procurado))
    : disponiveis;
  // Já existe com esse nome exato? Então Enter APLICA o que existe em vez de
  // criar outro com caixa diferente — é a divergência "Clientes"/"clientes"
  // que a lista existe para evitar.
  const jaExiste = todos.find((m) => m.toLowerCase() === procurado);

  return (
    <div className="relative" ref={caixa}>
      <button
        onClick={() => setAberto((a) => !a)}
        aria-label="Marcadores"
        title="Marcadores"
        aria-haspopup="menu"
        aria-expanded={aberto}
        className="rounded-full p-2 hover:bg-fundo"
      >
        <Icone nome="Tag" tamanho={18} />
      </button>

      {aberto && (
        <div
          role="menu"
          // left-0 e não right-0: o botão fica na borda ESQUERDA da barra de
          // ações, e alinhar o menu pela direita dele o jogava por baixo da
          // barra lateral, cortado.
          className="absolute left-0 z-30 mt-1 w-60 rounded-xl border border-linha bg-white py-1 shadow-lg"
        >
          {doUsuario.map((m) => (
            <button
              key={m}
              role="menuitemcheckbox"
              aria-checked="true"
              onClick={() => alternar(m)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-fundo"
            >
              <Icone nome="Check" tamanho={14} className="text-acao" />
              <Etiqueta nome={m} />
            </button>
          ))}
          {/* A lista rola: uma caixa com trinta marcadores faria o menu
              passar da altura da tela e os de baixo ficariam inalcançáveis. */}
          <div className="max-h-56 overflow-y-auto">
            {filtrados.map((m) => (
              <button
                key={m}
                role="menuitemcheckbox"
                aria-checked="false"
                onClick={() => alternar(m)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-fundo"
              >
                <span className="w-3.5" />
                <Etiqueta nome={m} />
              </button>
            ))}
          </div>

          {/* Os três estados que o menu precisa dizer, e eles são
              diferentes: "ainda estou buscando", "você não tem nenhum" e
              "nenhum casa com o que você digitou". Um texto só para os três
              faria a caixa nova parecer quebrada. */}
          {daCaixa.isPending && disponiveis.length === 0 && (
            <p className="px-3 py-2 text-xs text-tinta-fraca">carregando…</p>
          )}
          {!daCaixa.isPending && todos.length === 0 && (
            <p className="px-3 py-2 text-xs text-tinta-fraca">
              Você ainda não tem marcadores. Escreva um abaixo e aperte Enter.
            </p>
          )}
          {!daCaixa.isPending && todos.length > 0 && filtrados.length === 0 && procurado && (
            <p className="px-3 py-2 text-xs text-tinta-fraca">
              Nenhum com esse trecho. Enter cria “{novo.trim()}”.
            </p>
          )}

          <div className="border-t border-linha px-2 pt-2">
            <input
              value={novo}
              onChange={(e) => setNovo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setAberto(false);
                  return;
                }
                if (e.key !== "Enter" || !novo.trim()) return;
                // Casa com um que já existe? Aplica AQUELE, com a grafia
                // dele. Criar "clientes" ao lado de "Clientes" é a
                // divergência que a lista existe para evitar — e o IMAP
                // trata as duas como keywords diferentes de verdade.
                //
                // Espaço no meio é aceito pelo IMAP mas quebra clientes
                // antigos, que separam keywords por espaço. Trocar por traço
                // é menos pior que ver o marcador virar dois.
                alternar(jaExiste ?? novo.trim().replace(/\s+/g, "-"));
                setNovo("");
              }}
              placeholder={
                todos.length > 0 ? "Buscar ou criar, e Enter" : "Novo marcador e Enter"
              }
              aria-label="Buscar um marcador ou criar um novo"
              className="w-full rounded border border-linha px-2 py-1 text-sm"
            />
          </div>
          {erro && <p className="px-3 py-1 text-xs text-perigo">{erro}</p>}
        </div>
      )}
    </div>
  );
}
